import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'

import { editApplicantMutation } from '@/lib/queries/business/v1/applicant/mutations'
import type { ResApplicantDetail } from '@/lib/queries/business/v1/types'

import { formSchema } from './validation'

export const useContent = ({
  pathId,
  applicant,
  onClose,
  onEdited,
}: {
  pathId: string
  applicant: ResApplicantDetail
  onClose: () => void
  onEdited?: () => Promise<void>
}) => {
  const { mutate: editApplicant, isPending: isPendingEditApplicant } = useMutation(editApplicantMutation)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: applicant.applicant_name,
      email: applicant.email,
      phone: applicant.phone,
      tags: applicant.tags?.join(', ') ?? '',
    },
  })
  const formErrors = form.formState.errors

  const handleSubmit = form.handleSubmit((data) => {
    const tagsSplit = data.tags?.split(/\s*,\s*/) ?? []
    const tagsTrimmed = tagsSplit.map((x) => x.trim())
    const tagsFiltered = tagsTrimmed.filter((x) => x.length > 0)
    const setTags = new Set(tagsFiltered)
    const tags = [...setTags]

    editApplicant(
      {
        pathId,
        name: data.name,
        email: data.email,
        phone: data.phone,
        tags,
      },
      {
        onSuccess: async () => {
          await onEdited?.()
          toast.success('Applicant edited successfully.')
          form.reset()
          onClose()
        },
        onError: () => {
          toast.error('Unable to edit the applicant. Please try again later.')
        },
      }
    )
  })

  return {
    form,
    formErrors,
    isPendingEditApplicant,
    handleSubmit,
  }
}
